import { useState } from "react"
import { Check, ChevronsUpDown, Cpu, Lock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"

export interface ModelOption {
  /** Identifier the server understands — the provider's model id, e.g. 'openai/gpt-5.5'. */
  identifier: string
  label: string
  /** Not included in the current plan: shown with a lock, routes to upgrade. */
  locked?: boolean
}

export interface ModelPickerProps {
  value: string | null
  options: ModelOption[]
  onChange: (identifier: string) => void
  /** Picking a locked model. Fires instead of onChange, so the selection is untouched. */
  onLockedSelect?: (identifier: string) => void
  /** Hide the label on narrow layouts, where the chrome row runs out of width. */
  compact?: boolean
  className?: string
}

/**
 * Picks which model answers, for this conversation and the tools it drives —
 * code generation, backtest and optimizer config all run on the same choice.
 *
 * A popover anchored to its own trigger, matching the effort selector beside it:
 * the two controls sit together in the composer chrome, so opening one should
 * not feel heavier than opening the other.
 *
 * The server is the authority. It only accepts identifiers from its own
 * catalogue and ignores this entirely when the picker is switched off
 * (ChatModels.MODEL_PICKER_ENABLED), so nothing here is a permission boundary.
 */
export function ModelPicker({
  value,
  options,
  onChange,
  onLockedSelect,
  compact = false,
  className,
}: ModelPickerProps) {
  // Controlled so a selection closes the menu. Items live inside PopoverContent,
  // so Radix's outside-click never fires for them — and a locked pick opens the
  // paywall dialog, which sits below this popover in the stacking order.
  const [open, setOpen] = useState(false)

  const current = options.find((o) => o.identifier === value) ?? options[0]

  const handleSelect = (option: ModelOption) => {
    setOpen(false)
    if (option.locked) onLockedSelect?.(option.identifier)
    else onChange(option.identifier)
  }

  // Nothing to choose between is not a choice. The parent hides this when the
  // picker is disabled; this covers the one-model case too.
  if (options.length < 2) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-caption text-muted-foreground",
          "hover:bg-hover hover:text-foreground cursor-pointer transition-colors",
          "motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100",
          className,
        )}
        aria-label={`Model: ${current?.label ?? "default"}`}
      >
        <Cpu size={13} strokeWidth={1.75} className="shrink-0" />
        {!compact && current?.label}
        <ChevronsUpDown size={11} strokeWidth={1.75} className="shrink-0 opacity-60" />
      </PopoverTrigger>
      {/* The catalogue grows with every provider we add, so the list scrolls
          instead of the popover growing past the viewport. */}
      <PopoverContent
        align="start"
        className="w-64 p-1 max-h-[min(16rem,50dvh)] overflow-y-auto overscroll-contain"
      >
        {options.map((option) => (
          <button
            key={option.identifier}
            type="button"
            onClick={() => handleSelect(option)}
            // Not aria-disabled: a locked row is actionable, it opens the plan picker.
            className={cn(
              "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer",
              "hover:bg-hover transition-colors",
            )}
          >
            <Check
              size={13}
              strokeWidth={2}
              className={cn(
                "shrink-0",
                option.identifier === value ? "text-foreground" : "opacity-0",
              )}
            />
            <span className="flex items-center gap-1.5 min-w-0 text-label text-foreground">
              <span className="truncate">{option.label}</span>
              {option.locked && (
                <>
                  <Lock
                    size={11}
                    strokeWidth={2}
                    className="shrink-0 text-warning"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Upgrade required</span>
                </>
              )}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
